import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { userRoleData } from 'redux_store/slices/userRole';
import { roles } from 'constants/constant';
import { queuePreferredLanguage } from 'utils/preferredLanguage';

export const LanguageToggle = ({
  className = '',
  variant = 'default',
  forceExpanded = false,
}) => {
  const { i18n, t } = useTranslation(['common']);
  const { userRole } = useSelector(userRoleData);
  const isEn = i18n.language === 'en';

  const toggleLanguage = (e) => {
    e.stopPropagation();
    const next = isEn ? 'fr' : 'en';
    i18n.changeLanguage(next);
    // Persist per scope (debounced): consumers → borrowers row, every other
    // authenticated role (officer/processor/org/branch/super admin) → users
    // row. Unauthenticated contexts (login pages render this toggle too)
    // queue nothing — the switch stays UI-only.
    if (userRole === roles.consumer) {
      queuePreferredLanguage(next, 'b2c');
    } else if (userRole) {
      queuePreferredLanguage(next, 'b2b');
    }
  };

  if (variant === 'default') {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <div
          onClick={toggleLanguage}
          className="relative w-20 h-9 bg-[#3c588b] rounded-full cursor-pointer p-1 shadow-inner hover:opacity-90 transition-opacity select-none"
          title={isEn ? t('common:switchToFrench') : t('common:switchToEnglish')}
        >
          <div
            className={`
              absolute top-1 left-1 bottom-1 w-9 bg-white rounded-full shadow-md transition-transform duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]
              ${!isEn ? 'translate-x-[36px]' : 'translate-x-0'}
            `}
          />

          <div className="absolute inset-0 flex items-center justify-between px-1 pointer-events-none">
            <span
              className={`z-10 text-[11px] font-extrabold tracking-wider transition-colors duration-300 w-9 text-center ${
                isEn ? 'text-[#3c588b]' : 'text-white'
              }`}
            >
              EN
            </span>
            <span
              className={`z-10 text-[11px] font-extrabold tracking-wider transition-colors duration-300 w-9 text-center ${
                !isEn ? 'text-[#3c588b]' : 'text-white'
              }`}
            >
              FR
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'sidebar') {
    // Sidebar collapsed (narrow) → vertical pill (default at all viewport sizes).
    // Sidebar expanded (lg+ hover/focus-within, OR notifications open at any width) → horizontal pill.
    const verticalToggleVisibility = forceExpanded
      ? 'hidden'
      : 'flex lg:group-hover:hidden lg:group-focus-within:hidden';
    const horizontalToggleVisibility = forceExpanded
      ? 'flex'
      : 'hidden lg:group-hover:flex lg:group-focus-within:flex';

    return (
      <>
        <div
          onClick={toggleLanguage}
          className={`
            ${verticalToggleVisibility}
            relative w-[36px] h-[64px] bg-[#3c588b] rounded-full cursor-pointer p-1 shadow-inner
            hover:opacity-90 transition-all select-none flex-col items-center justify-between flex-shrink-0
            ${className}
          `}
          title={isEn ? t('common:switchToFrench') : t('common:switchToEnglish')}
        >
          <div
            className={`
              absolute left-1 w-[28px] h-[28px] bg-white rounded-full shadow-md transition-transform duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]
              ${!isEn ? 'translate-y-[28px]' : 'translate-y-0'}
            `}
          />

          <div className="z-10 w-full h-[28px] flex items-center justify-center pointer-events-none">
            <span
              className={`text-[10px] font-extrabold transition-colors duration-300 ${
                isEn ? 'text-[#3c588b]' : 'text-white'
              }`}
            >
              EN
            </span>
          </div>
          <div className="z-10 w-full h-[28px] flex items-center justify-center pointer-events-none">
            <span
              className={`text-[10px] font-extrabold transition-colors duration-300 ${
                !isEn ? 'text-[#3c588b]' : 'text-white'
              }`}
            >
              FR
            </span>
          </div>
        </div>

        <div
          onClick={toggleLanguage}
          className={`
            ${horizontalToggleVisibility}
            relative w-[74px] h-[32px] bg-[#3c588b] rounded-full cursor-pointer p-1 shadow-inner
            hover:opacity-90 transition-all select-none flex-shrink-0
            ${className}
          `}
          title={isEn ? t('common:switchToFrench') : t('common:switchToEnglish')}
        >
          <div
            className={`
              absolute top-1 left-1 bottom-1 w-[33px] bg-white rounded-full shadow-md transition-transform duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]
              ${!isEn ? 'translate-x-[33px]' : 'translate-x-0'}
            `}
          />

          <div className="absolute inset-0 flex items-center justify-between px-1 pointer-events-none">
            <span
              className={`z-10 text-[10px] font-extrabold tracking-wider transition-colors duration-300 w-[33px] text-center ${
                isEn ? 'text-[#3c588b]' : 'text-white'
              }`}
            >
              EN
            </span>
            <span
              className={`z-10 text-[10px] font-extrabold tracking-wider transition-colors duration-300 w-[33px] text-center ${
                !isEn ? 'text-[#3c588b]' : 'text-white'
              }`}
            >
              FR
            </span>
          </div>
        </div>
      </>
    );
  }

  return null;
};
